import { Logger } from '@core/model/domain/logger';
import { WebauthCompletionMode } from '@core/model/domain/webauthCompletionMode';
import { Option } from '@core/model/framework/Result/Option';
import { Result } from '@core/model/framework/Result/Result';
import { GetBankById } from '@core/usecase/getBankById';
import { GetWebauthUrl } from '@core/usecase/getWebauthUrl';
import { isProbablyTouchDevice } from '@shared/models/crossDeviceCapability';
import { WebauthRelayFragment, parseWebauthRelayFragment } from './webauthRelayFragment';

/*
  Debug aid, local runs only: how long the page holds, exactly as opened,
  before it does anything — fragment still in the url, opener not yet looked
  at, nothing called. Time to inspect it. Never on a deployed front.
*/
export const LOCAL_INSPECTION_HOLD_IN_MS = 10_000;

/*
  The page stays on screen at least this long before the browser is sent to
  the bank — whatever the speed of the bank url call — so the user sees their
  bank and that a redirection is in progress, rather than a flash.
*/
export const MINIMUM_DISPLAY_IN_MS = 4_000;

/*
  On a touch device the bank app may hand back to the DEFAULT browser — not
  necessarily the one this relay lives in. The mechanism below would route
  those journeys to the partner (whose stateless restore url resumes
  anywhere). It is deliberately OFF — product decision: whatever browser the
  bank lands in, the user is guided back to the page that started the journey
  (the widget outcome page says so), and that page has kept polling and moved
  on. Kept as a switch, not removed: the trade-off may be revisited, and the
  code path stays tested.
*/
const PARTNER_RESTORE_ON_TOUCH_DEVICES_ENABLED = false;

/* What the page shows while it fetches the bank url: whom the user is being sent to. */
export type RelayBank = { name: string; logo: Option<string> };

/*
  The landing page of the tab (or in-place navigation) opened on bank choice.

  It is AUTONOMOUS: everything it needs travels in its URL fragment, so it
  behaves the same whether it runs in a real tab, in a webview that loaded it
  in place, or in a browser opened by the host app — it never waits to be
  driven and never reads the (partitioned) sessionStorage.

  - `autonomous` fragment: fetch the bank url, choosing the completion mode
    from whether a page stays alive to poll (a live opener, or the origin
    having promised to survive), then redirect.
  - anything else, or a failure: send the window to the failure outcome page.
*/
export class WebauthRedirectVM {
  /* Best effort display: null until known, or when the bank could not be loaded. */
  bank: Option<RelayBank> = null;

  constructor(
    private readonly getWebauthUrl: GetWebauthUrl,
    private readonly getBankById: GetBankById,
    private readonly loggerService: Logger
  ) {}

  async onInit(): Promise<void> {
    await this.holdForLocalInspection();
    const fragment = this.takeFragment();
    void this.showBank(fragment);
    await this.drive(fragment);
  }

  /*
    Local runs only: the page stays as opened, doing nothing, long enough to
    look at it. Everything else — reading the fragment, the opener, the bank
    url — waits for this.
  */
  async holdForLocalInspection(): Promise<void> {
    if (!this.isLocalRun()) {
      return;
    }

    await this.wait(LOCAL_INSPECTION_HOLD_IN_MS);
  }

  /*
    Shows the bank the user picked (public bank catalogue, no token needed)
    while the bank url is fetched: the user sees their bank and that a
    redirection is in progress. Best effort — the journey never waits for a
    logo, and a missing one is no failure.
  */
  async showBank(fragment: WebauthRelayFragment): Promise<void> {
    if (fragment.kind !== 'autonomous') {
      return;
    }

    try {
      const bank = Result.unwrap(await this.getBankById.execute({ bankId: fragment.bankId }));
      this.bank = { value: { name: bank.name, logo: bank.logo ? { value: bank.logo } : null } };
    } catch {
      this.loggerService.logTrace('WEBAUTH_REDIRECT: bank could not be loaded for display');
    }
  }

  /*
    Reads the fragment then strips it from the history entry, synchronously.
    The fragment carries the user token, so this must run BEFORE anything can
    read window.location.href — telemetry (trackPage) captures the url, and the
    fragment would otherwise be beaconed to App Insights before the purge.
    (The fragment is never sent to the server on the wire to begin with.)
  */
  takeFragment(): WebauthRelayFragment {
    const fragment = parseWebauthRelayFragment(this.readLocationHash());
    this.purgeFragment();
    return fragment;
  }

  async drive(fragment: WebauthRelayFragment): Promise<void> {
    if (fragment.kind === 'autonomous') {
      this.partnerDeclaredOriginSurvives = fragment.originSurvives || this.queryCarriesSurvivalMarker();
      await this.fetchAndRedirect(fragment);
      return;
    }

    this.loggerService.logTrace('WEBAUTH_REDIRECT: no journey data in the fragment');
    this.redirectToFailureOutcome();
  }

  private partnerDeclaredOriginSurvives = false;

  private async fetchAndRedirect(fragment: {
    token: string;
    bankId: string;
    journeyKey: string;
    form: Parameters<GetWebauthUrl['execute']>[0]['form'];
  }): Promise<void> {
    // Started before the call: the display time counts from now, not from the answer.
    const minimumDisplay = this.holdForMinimumDisplay();

    try {
      const result = await this.getWebauthUrl.execute({
        tagging: undefined,
        userToken: fragment.token,
        bankId: fragment.bankId,
        form: fragment.form,
        // Forwarded untouched: it is the origin's handle on this journey.
        journeyKey: fragment.journeyKey,
        mode: this.decideMode(),
      });

      await minimumDisplay;
      this.redirectCurrentWindowTo(Result.unwrap(result).url);
    } catch (error) {
      // A failure is shown at once: there is no bank to announce anymore.
      this.loggerService.logException(error);
      this.redirectToFailureOutcome();
    }
  }

  /* Seam: the minimum time the page stays on screen before the bank. */
  holdForMinimumDisplay(): Promise<void> {
    return this.wait(MINIMUM_DISPLAY_IN_MS);
  }

  private wait(durationInMs: number): Promise<void> {
    return new Promise<void>((resolve) => setTimeout(resolve, durationInMs));
  }

  /*
    Where the journey lands once the bank is done — who resumes it:

    - server_callback (the widget outcome page, the origin page resumes by
      polling) needs TWO things: a page that stays alive to poll — a live
      opener proves the origin browsing context is still there (failing that,
      only the partner can vouch the origin survives, see below) — AND a bank
      that comes back into THIS browser. A computer gives both: the bank page
      lives in this very tab.
    - partner_restore otherwise. Either the origin was destroyed (a webview
      loaded the bank in place: no opener, no vouching) — or, flag on, this is
      a touch device: there the bank url opens the bank APP, which hands back
      to the default browser, not necessarily the one the origin lives in
      (started in Chrome, back in Safari). The partner restore url is
      stateless — it names the case server-side — so the partner resumes
      wherever the browser lands; the origin, if alive, learns it from the
      journey status and steps aside. Flag off: a live origin resumes, as on
      a computer.
  */
  private decideMode(): WebauthCompletionMode {
    /*
      The partner vouching beats every heuristic: it kept the origin alive ON
      PURPOSE (app2app through an external browser) precisely so the origin
      resumes by polling — asking the partner restore here would reload a
      widget the origin is about to resume itself. Wherever the bank lands,
      the origin polls: the touch rule does not apply.
    */
    if (this.partnerGuaranteesOriginSurvives()) {
      return 'server_callback';
    }

    const originSurvives = this.hasLiveOpener();
    const bankMayComeBackElsewhere = PARTNER_RESTORE_ON_TOUCH_DEVICES_ENABLED && this.isTouchDevice();

    if (!originSurvives || bankMayComeBackElsewhere) {
      return 'partner_restore';
    }

    return 'server_callback';
  }

  /*
    Whether the partner declared the origin page survives even though this
    relay may have no opener.

    Why a partner is ever in that position: the bank url is a universal link,
    and it only launches the bank APP (app2app) from a real browser — in a
    webview it falls back to the bank's web page. So a native partner app
    that wants app2app must intercept the open and push this page to an
    external browser (Safari, Chrome), keeping its webview — the origin —
    alive. From here that shows as no opener (another process), yet the
    origin lives and polls.

    The partner declares it AT BANK-OPEN TIME — the moment it makes that
    choice — by appending `originSurvives=1` to the url it opens; the origin
    itself never asserts it (it cannot foresee, when it builds the fragment,
    what the partner will do).
  */
  partnerGuaranteesOriginSurvives(): boolean {
    return this.partnerDeclaredOriginSurvives;
  }

  /*
    The marker is accepted in the query too (?originSurvives=1 before the #):
    appending to the very end of the url is the documented contract, but a
    native layer inserting a query param is just as natural — both are read.
    The query never carries anything sensitive, so appearing in access logs
    is fine.
  */
  private queryCarriesSurvivalMarker(): boolean {
    return new URLSearchParams(this.readLocationSearch()).get('originSurvives') === '1';
  }

  /* Seam: window.location is unforgeable in test environments. */
  readLocationSearch(): string {
    return window.location.search;
  }

  /* Seam: window.location.hash is unforgeable in test environments. */
  readLocationHash(): string {
    return window.location.hash;
  }

  /* Seam: strips the fragment from the current history entry. */
  purgeFragment(): void {
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  /* Seam: a live opener means the origin browsing context is still there. */
  hasLiveOpener(): boolean {
    return window.opener != null;
  }

  /* Seam: a phone or tablet, where the bank may come back in another browser. */
  isTouchDevice(): boolean {
    return isProbablyTouchDevice();
  }

  /* Seam: a front served from the developer's machine. */
  isLocalRun(): boolean {
    return ['localhost', '127.0.0.1'].includes(window.location.hostname);
  }

  /* Seam: window.location is unforgeable in test environments. */
  redirectCurrentWindowTo(url: string): void {
    window.location.replace(url);
  }

  /*
    Seam: no session to fall back on here (a secondary tab has none, an in-place
    webview lost the partitioned one), so the failure is shown by the outcome
    page rather than by navigating into the journey.
  */
  redirectToFailureOutcome(): void {
    window.location.replace(`${window.location.origin}/webauth?outcome=failure`);
  }
}
